/**
 * AmenitiesList Component
 *
 * Two-column list of community and apartment amenities.
 * Features: show more/less toggle, styled bullets, hover effects.
 */

import { useState, useMemo } from 'react';
import {
  useContentToken,
  useCollectionBinding,
  EditableToken,
  EditableCollectionBinding,
} from '@vibesites/runtime/react';
import type { ComponentContentSchema, CollectionBindingDefinition } from '@vibesites/runtime';
import type { Amenity } from '@vibesites/schema';
import { ChevronDownIcon } from '@vibesites/assets/icons/ui';

const VISIBLE_COUNT = 8;

export const contentSchema: ComponentContentSchema = {
  slot: 'AmenitiesList',
  displayName: 'Amenities List',
  tokens: {
    'amenitieslist.title': {
      label: 'Section Title',
      type: 'text',
      description: 'Main heading for the amenities section',
      defaultValue: 'AMENITIES THAT MAKE LIFE EASY.',
    },
    'amenitieslist.community_title': {
      label: 'Community Title',
      type: 'text',
      description: 'Heading for community amenities column',
      defaultValue: 'Community Amenities',
    },
    'amenitieslist.apartment_title': {
      label: 'Apartment Title',
      type: 'text',
      description: 'Heading for apartment amenities column',
      defaultValue: 'Apartment Amenities',
    },
  },
  collectionBindings: {
    'amenitieslist.items': {
      label: 'Amenities',
      collection: 'amenities',
      description: 'Property amenities list (filtered by group: community/apartment)',
      allowCustomItems: true,
    } as CollectionBindingDefinition,
  },
};

function stripHtml(html: string): string {
  if (typeof document !== 'undefined') {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || '').trim();
  }
  return html.replace(/<[^>]*>/g, '').trim();
}

function amenityLabel(amenity: Amenity): string {
  const description = stripHtml(amenity.description || '');
  if (description) return description;
  return (amenity.title || '').trim();
}

export function AmenitiesList() {
  const [showAllCommunity, setShowAllCommunity] = useState(false);
  const [showAllApartment, setShowAllApartment] = useState(false);

  const sectionTitle = useContentToken('amenitieslist.title');
  const communityTitle = useContentToken('amenitieslist.community_title');
  const apartmentTitle = useContentToken('amenitieslist.apartment_title');

  const allAmenities = useCollectionBinding<Amenity>('amenitieslist.items', {
    collection: 'amenities',
  });

  const { communityAmenities, apartmentAmenities } = useMemo(() => {
    const community = allAmenities.filter(a =>
      a.group?.toLowerCase() === 'community'
    );
    const apartment = allAmenities.filter(a =>
      a.group?.toLowerCase() === 'apartment'
    );
    return { communityAmenities: community, apartmentAmenities: apartment };
  }, [allAmenities]);

  const defaultCommunityAmenities = [
    'Brand New Community', 'Pet Friendly Community', 'Resident Lounge',
    'Fitness Studio', 'Rooftop Deck', 'Parcel Lockers',
    'EV Charging Stations', 'Gated Parking Garage',
  ];

  const defaultApartmentAmenities = [
    'Washer/Dryer', 'Wood-Style Flooring', 'Stainless Steel Appliances',
    'Quartz Counters', 'Spacious Closets', 'Smoke Free', 'Patios/Balconies',
  ];

  const displayCommunity = communityAmenities.length > 0
    ? communityAmenities.map(amenityLabel).filter(Boolean)
    : defaultCommunityAmenities;

  const displayApartment = apartmentAmenities.length > 0
    ? apartmentAmenities.map(amenityLabel).filter(Boolean)
    : defaultApartmentAmenities;

  const visibleCommunity = showAllCommunity ? displayCommunity : displayCommunity.slice(0, VISIBLE_COUNT);
  const visibleApartment = showAllApartment ? displayApartment : displayApartment.slice(0, VISIBLE_COUNT);

  const renderToggle = (
    items: string[],
    showAll: boolean,
    setShowAll: (v: boolean) => void,
  ) => {
    if (items.length <= VISIBLE_COUNT) return null;
    const remaining = items.length - VISIBLE_COUNT;
    return (
      <button
        onClick={() => setShowAll(!showAll)}
        className="flex items-center gap-1.5 text-primary-foreground font-medium text-sm mt-3 hover:text-primary-foreground/80 transition-colors duration-200 bg-transparent border-none cursor-pointer p-0 underline underline-offset-2"
      >
        {showAll ? 'Show Less' : `Show ${remaining} More`}
        <ChevronDownIcon
          size={14}
          className={`transition-transform duration-200 ${showAll ? 'rotate-180' : ''}`}
        />
      </button>
    );
  };

  return (
    <section className="bg-primary py-16 md:py-24" id="amenities" style={{ scrollMarginTop: 'calc(var(--header-height, 64px) + var(--offer-banner-height, 0px))' }}>
      <div className="max-w-[1200px] mx-auto px-4 md:px-6">
        <EditableToken
          path="amenitieslist.title"
          type="text"
          label="Section Title"
          as="div"
          style={{ marginBottom: '3rem' }}
        >
          <h2 className="text-[clamp(1.75rem,3vw,2.5rem)] font-bold font-heading text-primary-foreground uppercase m-0">
            {sectionTitle}
          </h2>
        </EditableToken>

        <EditableCollectionBinding bindingPath="amenitieslist.items" label="Amenities">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
            <div className="flex flex-col gap-6">
              <EditableToken path="amenitieslist.community_title" type="text" label="Community Title" as="div">
                <h3 className="text-2xl font-semibold font-heading text-primary-foreground m-0">{communityTitle}</h3>
              </EditableToken>

              <ul className="list-none p-0 m-0 flex flex-col gap-1">
                {visibleCommunity.map((amenity, index) => (
                  <li
                    key={index}
                    className="flex items-center gap-3 text-base leading-normal text-primary-foreground py-2 px-3 rounded-md hover:bg-primary-foreground/5 transition-colors duration-150"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-foreground shrink-0" />
                    <span>{amenity}</span>
                  </li>
                ))}
              </ul>
              {renderToggle(displayCommunity, showAllCommunity, setShowAllCommunity)}
            </div>

            <div className="flex flex-col gap-6">
              <EditableToken path="amenitieslist.apartment_title" type="text" label="Apartment Title" as="div">
                <h3 className="text-2xl font-semibold font-heading text-primary-foreground m-0">{apartmentTitle}</h3>
              </EditableToken>

              <ul className="list-none p-0 m-0 flex flex-col gap-1">
                {visibleApartment.map((amenity, index) => (
                  <li
                    key={index}
                    className="flex items-center gap-3 text-base leading-normal text-primary-foreground py-2 px-3 rounded-md hover:bg-primary-foreground/5 transition-colors duration-150"
                  >
                    <span className="w-1.5 h-1.5 rounded-full bg-primary-foreground shrink-0" />
                    <span>{amenity}</span>
                  </li>
                ))}
              </ul>
              {renderToggle(displayApartment, showAllApartment, setShowAllApartment)}
            </div>
          </div>
        </EditableCollectionBinding>
      </div>
    </section>
  );
}

(AmenitiesList as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AmenitiesList;
